import { useCallback, useEffect, useRef, useState } from 'react';

import { asApiError, type PSErrorClass } from '@/src/api/apiError';
import membershipsApi, {
  type MembershipDetail,
} from '@/src/api/membershipsApi';

/**
 * useFetchMembership — `GET /me/memberships/{id}`, behind `app/my-memberships/[id]`.
 *
 * The sibling of [[useFetchMyMemberships]], keyed by the id in the route. Same
 * three-state union and the same `retry` / `refresh` pair.
 *
 * `refresh` is what a cancel comes back to: `DELETE` answers 204 with no body,
 * so re-reading this endpoint is the only way to see the new state — and it
 * leaves the page drawn while it re-asks, which `retry` deliberately doesn't.
 */

export type FetchMembershipState = SettledState & {
  retry: () => void;
  refresh: () => Promise<void>;
};

type SettledState =
  | { status: 'loading'; membership: null; error: null }
  | { status: 'error'; membership: null; error: PSErrorClass }
  | { status: 'success'; membership: MembershipDetail; error: null };

const LOADING: SettledState = {
  status: 'loading',
  membership: null,
  error: null,
};

export function useFetchMembership(id: string): FetchMembershipState {
  const [state, setState] = useState<SettledState>(LOADING);
  const [attempt, setAttempt] = useState(0);
  const isMounted = useRef(true);

  const load = useCallback(async () => {
    try {
      const membership = await membershipsApi.getMyMembership(id);
      if (isMounted.current)
        setState({ status: 'success', membership, error: null });
    } catch (error: unknown) {
      if (isMounted.current)
        setState({
          status: 'error',
          membership: null,
          error: asApiError(error),
        });
    }
  }, [id]);

  useEffect(() => {
    isMounted.current = true;
    // False positive: `load` only setStates after awaiting the request, so the
    // write lands in a later microtask, not synchronously in this body.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();

    return () => {
      isMounted.current = false;
    };
  }, [attempt, load]);

  // A counter rather than a bare re-run: a retry that fails the SAME way must
  // still arrive as a fresh `PSErrorClass`.
  const retry = useCallback(() => {
    setState(LOADING);
    setAttempt((n) => n + 1);
  }, []);

  return { ...state, retry, refresh: load };
}
